// panels/pvc/PvcSystem.js —— 光储充 · 3.6 系统管理（需求文档 p31–p39）
// 3.6.1 电站信息管理 / 3.6.2 设备管理 + 强电结构 / 3.6.3 日志管理（操作·动环·EMS 运行）
// 3.6.4 EMS 时段管理（尖峰平谷深谷）/ 3.6.5 电量矫正 / 3.6.6 电量与收益重算
// 3.6.7 电价管理（电网电价·光伏发电电价）/ 3.6.8 高级功能 / 3.6.9 告警清理
import React, { useState } from 'react';
import ScrollTabs from '../../../../components/ScrollTabs';
import dayjs from 'dayjs';
import { Table, Button, Select, InputNumber, Input, DatePicker, Switch, Tag, Modal, Space, Alert, Radio, Checkbox, message,
} from 'antd';
import { EditOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import {
  getDevices, LOGS, getAdvancedFeatures, getWritePoints,
  getSecPwdPolicy, SEC_PWD_RECORDS, EMS_RESET_RECORDS,
} from '../../../../config/stationData';
import './pvc.css';

// 电量矫正 · 分时电量演示数据（前一天 / 当天 / 后一天，后一天为空行）
const CORR_PERIODS = ['尖', '峰', '平', '谷', '深谷'];
const CORR_DATA = {
  '2025-05-13': { charge: [0, 20.76001, 2.19, 32.66003, 0], discharge: [0, 0, 7.18005, 0.07001, 16.00999] },
  '2025-05-14': { charge: [0, 8.55005, 3.04001, 4.21997, 0], discharge: [0, 0, 6.30994, 27.71002, 0.62] },
  '2025-05-15': null,
};
const nowStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// 3.6.7 电价管理 · 电网电价（生效日期 × 买入/卖出电价，尖峰平谷深谷）
// 数据对齐文档截图：2025-03 行已填写（买入 1/0.8/0.3/0.1/0，卖出 1.6/1.4/1.1/0.5/0.1），其余月份为「请输入」占位
const TARIFF_GRID = [
  { key: 1, month: '2025-01', buy: [null, null, null, null, null], sell: [null, null, null, null, null] },
  { key: 2, month: '2025-02', buy: [null, null, null, null, null], sell: [null, null, null, null, null] },
  { key: 3, month: '2025-03', buy: [1, 0.8, 0.3, 0.1, 0], sell: [1.6, 1.4, 1.1, 0.5, 0.1] },
  { key: 4, month: '2025-04', buy: [null, null, null, null, null], sell: [null, null, null, null, null] },
  { key: 5, month: '2025-05', buy: [null, null, null, null, null], sell: [null, null, null, null, null] },
  { key: 6, month: '2025-06', buy: [null, null, null, null, null], sell: [null, null, null, null, null] },
  { key: 7, month: '2025-07', buy: [null, null, null, null, null], sell: [null, null, null, null, null] },
  { key: 8, month: '2025-08', buy: [null, null, null, null, null], sell: [null, null, null, null, null] },
  { key: 9, month: '2025-09', buy: [null, null, null, null, null], sell: [null, null, null, null, null] },
];

// ---------------- 动环管理 ----------------
const PvcDynEnvView = ({ dev }) => {
  const rows = [
    ...dev.hvac.map((d, i) => ({
      key: `ac${i}`, name: d.name, type: '空调', metric: '回风温度',
      value: `${(26.2 + i * 0.3).toFixed(1)} ℃`, state: i === 2 ? '通信中断' : '运行',
    })),
    { key: 'lc', name: dev.liquid.name, type: '液冷机组', metric: '出水温度', value: '22.4 ℃', state: '运行' },
    { key: 'th', name: '温湿度传感器 TH-01', type: '环境监测', metric: '温度 / 湿度', value: '25.6 ℃ / 58 %RH', state: '正常' },
    { key: 'wl', name: '水浸探测器 WL-01', type: '环境监测', metric: '积水状态', value: '无积水', state: '正常' },
    { key: 'sm', name: '烟感探测器 SM-01', type: '环境监测', metric: '烟雾状态', value: '无烟雾', state: '正常' },
  ];
  return (
    <div className="panel">
      <div className="panel-title">动环管理<small>机房环境与温控设备实时状态</small></div>
      <Table size="small" rowKey="key" pagination={false} dataSource={rows}
        columns={[
          { title: '设备名称', dataIndex: 'name' },
          { title: '类型', dataIndex: 'type', width: 130 },
          { title: '监测项', dataIndex: 'metric', width: 150 },
          { title: '当前值', dataIndex: 'value', width: 190 },
          { title: '状态', dataIndex: 'state', width: 120, render: (v) => <Tag color={v === '正常' || v === '运行' ? 'green' : 'red'}>{v}</Tag> },
          { title: '更新时间', width: 170, render: () => nowStr() },
        ]} />
    </div>
  );
};

// ---------------- 设备读写 ----------------
const PvcReadWriteView = ({ station }) => {
  const dev = getDevices(station.id);
  const list = [];
  dev.pcs.forEach((p) => list.push({ id: p.id, name: p.name, group: 'PCS' }));
  dev.racks.forEach((r) => list.push({ id: r.id, name: r.name, group: 'BMS' }));
  dev.hvac.forEach((a) => list.push({ id: a.id, name: a.name, group: '空调' }));
  list.push({ id: dev.liquid.id, name: dev.liquid.name, group: '液冷机组' });

  const [current, setCurrent] = useState(list[0].id);
  const [lastTime, setLastTime] = useState('2025-08-11 07:36:01');
  const [editing, setEditing] = useState({});
  const [pending, setPending] = useState({});
  const [override, setOverride] = useState({});

  const cur = list.find((d) => d.id === current) || list[0];
  const points = getWritePoints(cur.group).map((p, i) => ({
    ...p, key: i, cur: override[`${cur.id}-${i}`] !== undefined ? override[`${cur.id}-${i}`] : p.current,
  }));

  return (
    <div>
      <div className="dev-seg-bar" style={{ marginBottom: 12 }}>
        <span className="dev-kv-k">当前设备</span>
        <Select size="small" value={current} style={{ width: 220 }}
          onChange={(v) => { setCurrent(v); setEditing({}); setPending({}); }}
          options={list.map((d) => ({ value: d.id, label: d.name }))} />
        <Button type="primary" size="small" onClick={() => { setLastTime(nowStr()); setEditing({}); setPending({}); message.success('已重新读取点位当前值（演示）'); }}>查询</Button>
      </div>
      <div style={{ fontSize: 12, color: '#52e8b0', marginBottom: 12 }}>最后一次查询时间: {lastTime}</div>
      <div className="pvc-tip-warn" style={{ marginBottom: 14 }}>
        <div>如需对设备点参数进行写入，请联系技术人员进行配置，之后可在此界面操作</div>
        <div style={{ marginTop: 4 }}>为预防安全事故，策略运行时，请勿对 PCS、BMS 中的特殊点位进行写入操作</div>
      </div>
      <Table size="small" rowKey="key" pagination={false} dataSource={points}
        columns={[
          { title: '参数名称', dataIndex: 'name' },
          { title: '当前值', dataIndex: 'cur', width: 150, render: (v) => <span style={{ color: '#7df3d1' }}>{v}</span> },
          {
            title: '写入值', width: 190,
            render: (_, r) => (editing[r.key]
              ? <InputNumber size="small" style={{ width: 160 }} value={pending[r.key]}
                  onChange={(v) => setPending({ ...pending, [r.key]: v })} />
              : <span style={{ color: 'rgba(255,255,255,0.4)' }}>请输入</span>),
          },
          {
            title: '操作', width: 90,
            render: (_, r, i) => (editing[r.key]
              ? <Button size="small" type="primary" onClick={() => {
                  const v = pending[r.key];
                  if (v === undefined || v === null) { message.warning('请先填写写入值'); return; }
                  setOverride((o) => ({ ...o, [`${cur.id}-${i}`]: v }));
                  setEditing((e) => ({ ...e, [r.key]: false }));
                  message.success(`已写入「${r.name} = ${v}」（演示）`);
                }}>写入</Button>
              : <Button size="small" onClick={() => setEditing((e) => ({ ...e, [r.key]: true }))}>编辑</Button>),
          },
        ]} />
    </div>
  );
};

// ---------------- 二次口令 ----------------
const PvcSecPwdView = () => {
  const policy = getSecPwdPolicy();
  const [form, setForm] = useState({ old: '', next: '', confirm: '' });
  return (
    <>
      <div className="panel" style={{ maxWidth: 640 }}>
        <div className="panel-title">二次口令<small>控制指令下发与设备读写前需二次校验</small></div>
        <div className="pvc-param-row">
          <span className="dev-kv-k">原口令</span>
          <Input.Password value={form.old} onChange={(e) => setForm({ ...form, old: e.target.value })} style={{ width: 240 }} />
        </div>
        <div className="pvc-param-row">
          <span className="dev-kv-k">新口令</span>
          <Input.Password value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} style={{ width: 240 }} />
        </div>
        <div className="pvc-param-row">
          <span className="dev-kv-k">确认新口令</span>
          <Input.Password value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} style={{ width: 240 }} />
        </div>
        <div className="dev-seg-bar" style={{ marginTop: 12 }}>
          <Button type="primary" onClick={() => {
            if (!form.old || !form.next) { message.warning('请填写原口令与新口令'); return; }
            if (form.next !== form.confirm) { message.warning('两次输入的新口令不一致'); return; }
            message.success('二次口令已修改（演示）');
            setForm({ old: '', next: '', confirm: '' });
          }}>保存</Button>
        </div>
        <div className="pvc-tip-warn">
          口令规则：{policy.rule}；有效期 {policy.validDays} 天；连续错误 {policy.failLock} 次锁定 {policy.lockMinutes} 分钟。
        </div>
      </div>
      <div className="panel">
        <div className="panel-title">口令操作记录</div>
        <Table size="small" rowKey="key" pagination={false} dataSource={SEC_PWD_RECORDS}
          columns={[
            { title: '时间', dataIndex: 'time', width: 180 },
            { title: '用户', dataIndex: 'user', width: 100 },
            { title: '动作', dataIndex: 'action', width: 150 },
            { title: 'IP', dataIndex: 'ip', width: 140 },
            { title: '结果', dataIndex: 'result', render: (v) => <Tag color={v === '成功' ? 'green' : 'red'}>{v}</Tag> },
          ]} />
      </div>
    </>
  );
};

// ---------------- EMS复归 ----------------
const PvcEmsResetView = () => {
  const [recs, setRecs] = useState(EMS_RESET_RECORDS);
  return (
    <>
      <div className="panel">
        <div className="panel-title">EMS复归<small>故障处理后对 EMS 复位，恢复正常调度</small></div>
        <Button type="primary" icon={<ReloadOutlined />} onClick={() => {
          setRecs((r) => [{ key: r.length + 1, time: nowStr(), scope: '全站 EMS', user: 'admin', before: '策略停止 · 故障闭锁', result: '成功' }, ...r]);
          message.success('EMS 已复归（演示）');
        }}>EMS复归</Button>
        <div className="pvc-tip-warn">复归会清除当前故障闭锁状态并恢复 EMS 调度；请先确认设备故障已排除。</div>
      </div>
      <div className="panel">
        <div className="panel-title">复归记录</div>
        <Table size="small" rowKey="key" pagination={false} dataSource={recs}
          columns={[
            { title: '时间', dataIndex: 'time', width: 180 },
            { title: '范围', dataIndex: 'scope', width: 180 },
            { title: '用户', dataIndex: 'user', width: 100 },
            { title: '复归前状态', dataIndex: 'before' },
            { title: '结果', dataIndex: 'result', width: 90, render: (v) => <Tag color={v === '成功' ? 'green' : 'red'}>{v}</Tag> },
          ]} />
      </div>
    </>
  );
};

// ---------------- 个性配置 ----------------
const PvcPersonalView = () => {
  const [theme, setTheme] = useState('dark');
  const [home, setHome] = useState('overview');
  const [refresh, setRefresh] = useState(5);
  return (
    <div className="panel" style={{ maxWidth: 640 }}>
      <div className="panel-title">个性配置<small>界面主题与展示偏好</small></div>
      <div className="pvc-param-row">
        <span className="dev-kv-k">界面主题</span>
        <Radio.Group value={theme} onChange={(e) => setTheme(e.target.value)}
          options={[{ label: '深色', value: 'dark' }, { label: '浅色', value: 'light' }]} />
      </div>
      <div className="pvc-param-row">
        <span className="dev-kv-k">默认首页</span>
        <Select value={home} onChange={setHome} style={{ width: 180 }}
          options={[{ value: 'overview', label: '站点首页' }, { value: 'device', label: '设备监控' }, { value: 'report', label: '统计报表' }]} />
      </div>
      <div className="pvc-param-row">
        <span className="dev-kv-k">数据刷新间隔</span>
        <InputNumber value={refresh} min={1} max={60} onChange={setRefresh} style={{ width: 100 }} />
        <span>秒</span>
      </div>
      <div className="dev-seg-bar" style={{ marginTop: 12 }}>
        <Button type="primary" onClick={() => message.success('个性配置已保存（演示）')}>保存</Button>
      </div>
    </div>
  );
};

// ---------------- 高级功能 ----------------
const PvcAdvancedView = () => {
  const [p, setP] = useState({ overV: 850, underV: 600, overT: 45, overI: 120 });
  const fields = [['过压保护', 'overV', 'V'], ['欠压保护', 'underV', 'V'], ['过温保护', 'overT', '℃'], ['过流保护', 'overI', 'A']];
  return (
    <div className="panel" style={{ maxWidth: 700 }}>
      <div className="panel-title">高级功能<small>高级保护参数设置</small></div>
      {fields.map(([label, k, unit]) => (
        <div className="pvc-param-row" key={k}>
          <span className="dev-kv-k">{label}</span>
          <InputNumber value={p[k]} onChange={(v) => setP({ ...p, [k]: v })} style={{ width: 120 }} />
          <span>{unit}</span>
        </div>
      ))}
      <div className="dev-seg-bar" style={{ marginTop: 12 }}>
        <Button type="primary" onClick={() => message.success('高级参数已保存（演示）')}>保存</Button>
      </div>
      <div className="pvc-tip-warn">高级功能通常涉及重要保护规则，修改前请先咨询技术人员。</div>
    </div>
  );
};

// ---------------- 3.6.1 电站信息管理 ----------------
const PVC_INFO = {
  region: '其他国家',
  country: '日本',
  address: '新西湖小镇',
  timezone: '(UTC+08:00) 上海',
  currency: '美元',
  buildAt: '2025-03-21 17:37:30',
};
const pvcLabel = (text, required) => (
  <span className="pvc-info-label">{required ? <i className="pvc-req">*</i> : null}{text}：</span>
);

const PvcStationInfo = ({ station }) => {
  const [form, setForm] = useState({ ...PVC_INFO, name: station.name });
  const [editing, setEditing] = useState(false);
  const sel = (key, options) => (
    <Select size="small" style={{ width: 260 }} value={form[key]} disabled={!editing}
      onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
      options={options.map((o) => ({ value: o, label: o }))} />
  );
  return (
    <div className="panel" style={{ maxWidth: 800 }}>
      <div className="panel-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>电站信息管理</span>
        {editing ? (
          <Space size={8}>
            <Button size="small" type="primary"
              onClick={() => { setEditing(false); message.success('电站信息已保存（演示）'); }}>保存</Button>
            <Button size="small" onClick={() => setEditing(false)}>取消</Button>
          </Space>
        ) : <Button size="small" type="primary" onClick={() => setEditing(true)}>编辑</Button>}
      </div>
      <div className="pvc-info-sub">基础信息 ▾</div>
      <div className="pvc-info-row">
        {pvcLabel('电站名称', true)}
        <Input size="small" style={{ width: 260 }} value={form.name} disabled={!editing}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>
      <div className="pvc-info-row">{pvcLabel('电站地区', true)}{sel('region', ['中国', '其他国家'])}</div>
      <div className="pvc-info-row">{pvcLabel('电站国家', true)}{sel('country', ['中国', '日本'])}</div>
      <div className="pvc-info-row">
        {pvcLabel('详细地址', true)}
        <Input size="small" style={{ width: 260 }} value={form.address} disabled={!editing}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
      </div>
      <div className="pvc-info-row">{pvcLabel('时区', true)}{sel('timezone', ['(UTC+08:00) 上海', '(UTC+09:00) 东京'])}</div>
      <div className="pvc-info-row">{pvcLabel('货币单位', true)}{sel('currency', ['人民币', '美元'])}</div>
      <div className="pvc-info-row" style={{ alignItems: 'flex-start' }}>
        {pvcLabel('电站照片', false)}
        <div className="pvc-photo-box">暂无图片</div>
      </div>
      <div className="pvc-info-row" style={{ marginTop: 18 }}>
        <span className="pvc-info-label">建站时间：</span>
        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>{form.buildAt}</span>
      </div>
    </div>
  );
};

// ---------------- 3.6.2 设备管理 ----------------
const DEVICE_TYPE_OPTIONS = ['BMS', 'io控制器', '双向电表'];
const PVC_DEVICES = [
  { key: 'bms1', name: '1#bms', type: 'BMS', vendor: '/', driver: '-', comm: '-', unit: '-' },
  { key: 'dio1', name: '1#dio控制器', type: 'io控制器', vendor: '/', driver: '-', comm: '-', unit: '-' },
  { key: 'm1', name: '负载电表', type: '双向电表', vendor: '/', driver: '-', comm: '-', unit: '-' },
  { key: 'm2', name: '总光伏表', type: '双向电表', vendor: '/', driver: '-', comm: '-', unit: '-' },
  { key: 'm3', name: '总负载表', type: '双向电表', vendor: '/', driver: '-', comm: '-', unit: '-' },
  { key: 'm4', name: '虚拟总表', type: '双向电表', vendor: '/', driver: '-', comm: '-', unit: '-' },
  { key: 'm5', name: '1#光储配电柜电表', type: '双向电表', vendor: '/', driver: '-', comm: '-', unit: '-' },
];

// 强电节点（对齐 p033_i055）
const PVC_NODES = [
  { key: 't1', name: '1#变压器', type: '公共连接点', ref: '虚拟总表', removable: false },
  { key: 't2', name: '2#变压器', type: '变压器', ref: '虚拟总表', removable: true },
  { key: 's1', name: '1#储能节点', type: '储能节点', ref: '1#pcs', removable: true },
  { key: 'p1', name: '1#光伏节点', type: '光伏节点', ref: '1#光伏功率电表', removable: true },
  { key: 'l1', name: '1#负载节点', type: '负载节点', ref: '负载电表', removable: true },
];
const NODE_TYPE_OPTIONS = ['公共连接点', '变压器', '储能节点', '光伏节点', '负载节点'];

const PvcDeviceManage = () => {
  const [sub, setSub] = useState('list');
  // 设备列表
  const [list, setList] = useState(PVC_DEVICES);
  const [type, setType] = useState('全部');
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [dioTarget, setDioTarget] = useState(null);
  // 强电结构
  const [strongView, setStrongView] = useState('nodes');
  const [nodes, setNodes] = useState(PVC_NODES);
  const [nodeType, setNodeType] = useState('请选择');
  const [nodeQuery, setNodeQuery] = useState('请选择');

  const openEdit = (r) => {
    setEditTarget(r);
    setEditForm({
      type: r.type, name: r.name, meterCat: '一级电网表', ratio: '', vendor: '', lan: '', com: '', driver: '', ip: '', port: '', addr: '',
    });
  };
  const editRow = (label, required, node) => (
    <div className="pvc-info-row" style={{ marginBottom: 12 }}>
      <span className="pvc-info-label">{required ? <i className="pvc-req">*</i> : null}{label}：</span>
      {node}
    </div>
  );
  const showNodeRows = nodeQuery === '请选择' ? nodes : nodes.filter((n) => n.type === nodeQuery);

  return (
    <>
      <ScrollTabs
        activeKey={sub}
        onChange={setSub}
        items={[
          {
            key: 'list',
            label: '设备列表',
            children: (
              <>
                <div className="dev-seg-bar" style={{ marginBottom: 12 }}>
                  <span className="dev-kv-k">设备类型：</span>
                  <Select size="small" value={type} onChange={setType} style={{ width: 180 }}
                    options={['全部', ...DEVICE_TYPE_OPTIONS].map((t) => ({ value: t, label: t }))} />
                  <Button type="primary" size="small" onClick={() => message.info('已按设备类型查询（演示）')}>查询</Button>
                  <Button size="small" onClick={() => { setType('全部'); message.info('已重置查询条件'); }}>重置</Button>
                </div>
                <Table
                  size="small"
                  rowKey="key"
                  pagination={false}
                  dataSource={type === '全部' ? list : list.filter((r) => r.type === type)}
                  columns={[
                    { title: '设备名称', dataIndex: 'name' },
                    { title: '设备类型', dataIndex: 'type', width: 140 },
                    { title: '厂商·型号', dataIndex: 'vendor', width: 140 },
                    { title: '驱动', dataIndex: 'driver', width: 120 },
                    { title: '通信参数', dataIndex: 'comm', width: 140 },
                    { title: '所属单元', dataIndex: 'unit', width: 140 },
                    {
                      title: '操作',
                      width: 180,
                      render: (_, r) => (
                        <Space size={14}>
                          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openEdit(r)}>编辑设备</Button>
                          {r.type === 'io控制器' ? (
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setDioTarget(r)}>DIO信息</Button>
                          ) : null}
                        </Space>
                      ),
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'struct',
            label: '强电结构',
            children: (
              <>
                <div className="dev-seg-bar" style={{ marginBottom: 12 }}>
                  <Button type="primary" size="small" onClick={() => message.success('强电配置已投入，配置已生效（演示）')}>投入</Button>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>● 所有配置填写后，需点击投入，配置才会生效。</span>
                </div>
                <ScrollTabs
                  activeKey={strongView}
                  onChange={setStrongView}
                  items={[
                    {
                      key: 'nodes',
                      label: '强电节点',
                      children: (
                        <>
                          <div className="dev-seg-bar" style={{ marginBottom: 12, justifyContent: 'space-between' }}>
                            <Button size="small" onClick={() => message.info('新增节点（演示）')}>新增节点</Button>
                            <Space size={8}>
                              <span className="dev-kv-k">节点类型：</span>
                              <Select size="small" value={nodeType} onChange={setNodeType} style={{ width: 180 }}
                                options={['请选择', ...NODE_TYPE_OPTIONS].map((t) => ({ value: t, label: t }))} />
                              <Button type="primary" size="small" onClick={() => { setNodeQuery(nodeType); message.info('已按节点类型查询（演示）'); }}>查询</Button>
                            </Space>
                          </div>
                          <Table
                            size="small"
                            rowKey="key"
                            pagination={false}
                            dataSource={showNodeRows}
                            columns={[
                              { title: '节点名称', dataIndex: 'name' },
                              { title: '节点类型', dataIndex: 'type', width: 160 },
                              { title: '功率参考', dataIndex: 'ref', width: 200 },
                              {
                                title: '操作',
                                width: 140,
                                render: (_, r) => (
                                  <Space size={14}>
                                    {r.removable ? (
                                      <Button type="link" size="small" style={{ padding: 0 }}
                                        onClick={() => { setNodes((ns) => ns.filter((n) => n.key !== r.key)); message.success('节点已删除（演示）'); }}>删除</Button>
                                    ) : null}
                                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => message.info('编辑节点（演示）')}>编辑</Button>
                                  </Space>
                                ),
                              },
                            ]}
                          />
                        </>
                      ),
                    },
                    {
                      key: 'table',
                      label: '强电接线表',
                      children: (
                        <Table
                          size="small"
                          rowKey="key"
                          pagination={false}
                          dataSource={nodes.map((n) => ({ ...n, unit: n.ref, state: '已接入' }))}
                          columns={[
                            { title: '节点名称', dataIndex: 'name' },
                            { title: '节点类型', dataIndex: 'type', width: 160 },
                            { title: '下属单元', dataIndex: 'unit', width: 200 },
                            { title: '状态', dataIndex: 'state', width: 120, render: (v) => <Tag color="green">{v}</Tag> },
                            { title: '操作', width: 90, render: () => <Button type="link" size="small" style={{ padding: 0 }} onClick={() => message.info('已投入（演示）')}>投入</Button> },
                          ]}
                        />
                      ),
                    },
                    {
                      key: 'diagram',
                      label: '强电接线图',
                      children: <div className="pvc-strong-canvas">强电接线图（按强电节点配置自动生成）</div>,
                    },
                  ]}
                />
              </>
            ),
          },
        ]}
      />

      <Modal title="编辑设备" open={!!editTarget} width={620} okText="确定" cancelText="取消"
        onCancel={() => setEditTarget(null)}
        onOk={() => {
          if (editForm.name) {
            setList((ls) => ls.map((r) => (r.key === editTarget.key ? { ...r, name: editForm.name, type: editForm.type } : r)));
            message.success('设备信息已保存（演示）');
          }
          setEditTarget(null);
        }}
        destroyOnHidden>
        {editForm.type ? (
          <>
            {editRow('设备类型', true,
              <Select size="small" style={{ width: 260 }} value={editForm.type}
                onChange={(v) => setEditForm((f) => ({ ...f, type: v }))}
                options={DEVICE_TYPE_OPTIONS.map((t) => ({ value: t, label: t }))} />)}
            {editRow('设备名称', true,
              <Input size="small" style={{ width: 260 }} value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />)}
            {editRow('电表类别', true,
              <Select size="small" style={{ width: 260 }} value={editForm.meterCat}
                onChange={(v) => setEditForm((f) => ({ ...f, meterCat: v }))}
                options={['一级电网表', '二级电网表', '储能电表', '光伏电表', '负载电表'].map((t) => ({ value: t, label: t }))} />)}
            {editRow('电能示数比', true,
              <div>
                <Input size="small" style={{ width: 260 }} placeholder="请输入" value={editForm.ratio}
                  onChange={(e) => setEditForm((f) => ({ ...f, ratio: e.target.value }))} />
                <div className="pvc-edit-hint">电能与实际电表示数的比值</div>
              </div>)}
            {editRow('厂商型号', false,
              <Select size="small" style={{ width: 260 }} value={editForm.vendor || undefined} placeholder="请选择"
                onChange={(v) => setEditForm((f) => ({ ...f, vendor: v }))} options={['华为', '阳光电源', '科华'].map((t) => ({ value: t, label: t }))} />)}
            {editRow('LAN/COM', false,
              <Space size={8}>
                <span style={{ fontSize: 12 }}>LAN口数量：</span>
                <Input size="small" style={{ width: 110 }} placeholder="请输入" value={editForm.lan}
                  onChange={(e) => setEditForm((f) => ({ ...f, lan: e.target.value }))} />
                <span style={{ fontSize: 12 }}>COM口数量：</span>
                <Input size="small" style={{ width: 110 }} placeholder="请输入" value={editForm.com}
                  onChange={(e) => setEditForm((f) => ({ ...f, com: e.target.value }))} />
              </Space>)}
            {editRow('设备驱动', false,
              <Select size="small" style={{ width: 260 }} value={editForm.driver || undefined} placeholder="请选择"
                onChange={(v) => setEditForm((f) => ({ ...f, driver: v }))} options={['Modbus-TCP', 'Modbus-RTU', 'IEC104'].map((t) => ({ value: t, label: t }))} />)}
            {editRow('IP地址', false,
              <div>
                <Input size="small" style={{ width: 260 }} placeholder="请输入" value={editForm.ip}
                  onChange={(e) => setEditForm((f) => ({ ...f, ip: e.target.value }))} />
                <div className="pvc-edit-hint">Modbus—TCP接入请填写此参数</div>
              </div>)}
            {editRow('端口号', false,
              <div>
                <Input size="small" style={{ width: 260 }} placeholder="请输入" value={editForm.port}
                  onChange={(e) => setEditForm((f) => ({ ...f, port: e.target.value }))} />
                <div className="pvc-edit-hint">Modbus—TCP接入请填写此参数</div>
              </div>)}
            {editRow('设备地址', false,
              <Input size="small" style={{ width: 260 }} placeholder="请输入" value={editForm.addr}
                onChange={(e) => setEditForm((f) => ({ ...f, addr: e.target.value }))} />)}
          </>
        ) : null}
      </Modal>

      <Modal title={dioTarget ? `DIO 信息 · ${dioTarget.name}` : ''} open={!!dioTarget}
        onCancel={() => setDioTarget(null)}
        onOk={() => { setDioTarget(null); message.success('DIO 配置已保存（演示）'); }}
        okText="确定"
        cancelText="取消"
        width={620}
        destroyOnHidden>
        <div style={{ marginBottom: 10, color: '#8fa3bf', fontSize: 12 }}>
          填写每个端口连接的设备名称、通信状态含义，结果生效于「设备监控-消防安全」页面。
        </div>
        <Table
          size="small"
          rowKey="port"
          pagination={false}
          dataSource={[
            { port: 'DI1', name: '消防联动信号', s0: '断开', s1: '闭合' },
            { port: 'DI2', name: '急停输入', s0: '断开', s1: '闭合' },
            { port: 'DO1', name: '声光报警', s0: '停止', s1: '运行' },
            { port: 'DO2', name: '排风扇', s0: '停止', s1: '运行' },
          ]}
          columns={[
            { title: 'DIO 端口', dataIndex: 'port', width: 100 },
            { title: '设备名称', dataIndex: 'name', render: (v) => <Input size="small" defaultValue={v} /> },
            { title: '通信状态为 0', dataIndex: 's0', render: (v) => <Input size="small" defaultValue={v} /> },
            { title: '通信状态为 1', dataIndex: 's1', render: (v) => <Input size="small" defaultValue={v} /> },
          ]}
        />
      </Modal>
    </>
  );
};

// ---------------- 3.6.4 EMS 时段管理 ----------------
const PERIOD_KINDS = ['不计费', '尖', '峰', '平', '谷', '深谷'];
const KIND_COLOR = { 不计费: '#7a8699', 尖: '#ff4d4f', 峰: '#ffa940', 平: '#4dd8ff', 谷: '#52e8b0', 深谷: '#2fd8c9' };
const PVC_PERIODS = [
  { key: 1, start: '00:00', end: '08:00', kind: '谷' },
  { key: 2, start: '08:00', end: '11:00', kind: '峰' },
  { key: 3, start: '11:00', end: '13:00', kind: '谷' },
  { key: 4, start: '13:00', end: '17:00', kind: '峰' },
  { key: 5, start: '17:00', end: '24:00', kind: '平' },
];
const periodMin = (t) => { const [h, m] = String(t).split(':').map(Number); return h * 60 + (m || 0); };
const periodPct = (s, e) => Math.max(0, ((periodMin(e) - periodMin(s)) / 1440) * 100);
const PERIOD_CHANGES = [
  { key: 1, time: '2025-05-08 14:20', user: '袁铭皓', content: '谷时时段 00:00-08:00 配置保存' },
  { key: 2, time: '2025-04-15 09:10', user: '袁铭皓', content: '新增峰时时段 13:00-17:00' },
];

const PvcPeriodView = () => {
  const [tab, setTab] = useState('current');
  const [manage, setManage] = useState(PVC_PERIODS.map((p) => ({ ...p })));
  const up = (key, patch) => setManage((arr) => arr.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  return (
    <div className="panel">
      <ScrollTabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'current',
            label: '当前EMS时段',
            children: (
              <>
                <div className="pvc-period-axis">
                  {PVC_PERIODS.map((p) => (
                    <div key={p.key} className="pvc-period-seg" style={{ width: `${periodPct(p.start, p.end)}%`, background: KIND_COLOR[p.kind] }}>
                      <span>{p.kind}</span>
                    </div>
                  ))}
                </div>
                <div className="pvc-period-hours">
                  {Array.from({ length: 25 }, (_, i) => <span key={i} style={{ left: `${(i / 24) * 100}%` }}>{i}</span>)}
                </div>
                <div style={{ marginBottom: 14 }}>
                  {PERIOD_KINDS.map((k) => (
                    <span key={k} className="pvc-period-legend"><i style={{ background: KIND_COLOR[k] }} />{k}</span>
                  ))}
                </div>
                <Table
                  size="small" rowKey="key" pagination={false} dataSource={PVC_PERIODS}
                  columns={[
                    { title: '开始时间', dataIndex: 'start' },
                    { title: '结束时间', dataIndex: 'end' },
                    { title: '时段说明', dataIndex: 'kind', render: (v) => <span style={{ color: KIND_COLOR[v], fontWeight: 600 }}>{v}</span> },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'manage',
            label: '时段管理',
            children: (
              <>
                <div className="dev-seg-bar" style={{ marginBottom: 12 }}>
                  <Button size="small" onClick={() => setManage((arr) => [...arr, { key: Date.now(), start: '00:00', end: '01:00', kind: '谷' }])}>新增时段</Button>
                  <Button size="small" type="primary" onClick={() => message.success('EMS 时段已保存（演示）')}>保存</Button>
                </div>
                <Table
                  size="small" rowKey="key" pagination={false} dataSource={manage}
                  columns={[
                    { title: '时段说明', dataIndex: 'kind', width: 160, render: (v, r) => (
                      <Select size="small" value={v} style={{ width: 120 }} options={PERIOD_KINDS.map((k) => ({ value: k, label: k }))}
                        onChange={(x) => up(r.key, { kind: x })} />
                    ) },
                    { title: '开始时间', dataIndex: 'start', width: 140, render: (v, r) => (
                      <Input size="small" value={v} style={{ width: 96 }} onChange={(e) => up(r.key, { start: e.target.value })} />
                    ) },
                    { title: '结束时间', dataIndex: 'end', width: 140, render: (v, r) => (
                      <Input size="small" value={v} style={{ width: 96 }} onChange={(e) => up(r.key, { end: e.target.value })} />
                    ) },
                    { title: '操作', width: 90, render: (_, r) => (
                      <Button type="link" size="small" danger style={{ padding: 0 }}
                        onClick={() => setManage((arr) => arr.filter((x) => x.key !== r.key))}>删除</Button>
                    ) },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'record',
            label: '修改记录',
            children: (
              <Table
                size="small" rowKey="key" pagination={false} dataSource={PERIOD_CHANGES}
                columns={[
                  { title: '时间', dataIndex: 'time', width: 180 },
                  { title: '用户', dataIndex: 'user', width: 120 },
                  { title: '变更内容', dataIndex: 'content' },
                ]}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

const PvcSystem = ({ station }) => {
  const dev = getDevices(station.id);
  const adv = getAdvancedFeatures();

  // ---------- 3.6.5 电量矫正 ----------
  const [corrMeter, setCorrMeter] = useState(dev.meters[0].id);
  const [corrDate, setCorrDate] = useState(dayjs('2025-05-14'));
  const [corrEditing, setCorrEditing] = useState(false);
  const [corrDraft, setCorrDraft] = useState({ charge: [0, 8.55005, 3.04001, 4.21997, 0], discharge: [0, 0, 6.30994, 27.71002, 0.62] });
  const [corrSave, setCorrSave] = useState(false);
  const [corrRecalc, setCorrRecalc] = useState(true);
  const [corrLogs, setCorrLogs] = useState([]);

  // 电量矫正 · 三天（前一天 / 当天 / 后一天）数据行
  const prevKey = corrDate.subtract(1, 'day').format('YYYY-MM-DD');
  const curKey = corrDate.format('YYYY-MM-DD');
  const nextKey = corrDate.add(1, 'day').format('YYYY-MM-DD');
  const corrRows = [
    { key: 'prev', date: prevKey, charge: CORR_DATA[prevKey] && CORR_DATA[prevKey].charge, discharge: CORR_DATA[prevKey] && CORR_DATA[prevKey].discharge },
    { key: 'cur', date: curKey, charge: corrEditing ? corrDraft.charge : (CORR_DATA[curKey] && CORR_DATA[curKey].charge), discharge: corrEditing ? corrDraft.discharge : (CORR_DATA[curKey] && CORR_DATA[curKey].discharge), editable: corrEditing },
    { key: 'next', date: nextKey, charge: CORR_DATA[nextKey] && CORR_DATA[nextKey].charge, discharge: CORR_DATA[nextKey] && CORR_DATA[nextKey].discharge },
  ];
  const corrCell = (r, field, i) => {
    if (r.empty) return null;
    const v = r[field] && r[field][i];
    if (r.editable) {
      return (
        <InputNumber size="small" value={corrDraft[field][i]} min={0} style={{ width: 92 }}
          onChange={(val) => setCorrDraft((d) => ({ ...d, [field]: d[field].map((x, j) => (j === i ? (val ?? 0) : x)) }))} />
      );
    }
    return <span>{v ?? ''}</span>;
  };
  const corrColumns = [
    { title: '日期', dataIndex: 'date', width: 110 },
    { title: '充电量', children: CORR_PERIODS.map((p, i) => ({ title: p, align: 'right', width: 88, render: (_, r) => corrCell(r, 'charge', i) })) },
    { title: '放电量', children: CORR_PERIODS.map((p, i) => ({ title: p, align: 'right', width: 88, render: (_, r) => corrCell(r, 'discharge', i) })) },
  ];
  const corrMeterName = (dev.meters.find((m) => m.id === corrMeter) || dev.meters[0]).name;

  // ---------- 3.6.7 电价管理 ----------
  const [tariffTab, setTariffTab] = useState('grid');
  const [tariffYear, setTariffYear] = useState('2025');
  const [tariffEditing, setTariffEditing] = useState(false);
  const [tariffGrid, setTariffGrid] = useState(TARIFF_GRID);
  const [pvRecalc, setPvRecalc] = useState(true); // 是否用此电价重算历史收益（否/是），文档默认选中「是」
  const [pvPrice, setPvPrice] = useState(0.75);

  // ---------- 3.6.9 告警清理 ----------
  const [alertCount, setAlertCount] = useState(1000);

  // 3.6.7 电价管理 · 电网电价单元格：浏览态「请输入」占位 / 编辑态 InputNumber
  const tariffCell = (r, field, i) => {
    const v = r[field][i];
    if (tariffEditing) {
      return (
        <InputNumber size="small" value={v ?? null} step={0.01} min={0} style={{ width: 74 }}
          onChange={(val) => setTariffGrid((g) => g.map((x) => (x.key === r.key ? { ...x, [field]: x[field].map((y, j) => (j === i ? val : y)) } : x)))} />
      );
    }
    return v == null ? <span style={{ color: 'rgba(255,255,255,0.4)' }}>请输入</span> : <span>{v}</span>;
  };

  return (
    <>
      <ScrollTabs
        items={[
          {
            key: 'info',
            label: '电站信息',
            children: <PvcStationInfo station={station} />,
          },
          {
            key: 'device',
            label: '设备管理',
            children: <PvcDeviceManage dev={dev} />,
          },
          {
            key: 'log',
            label: '日志管理',
            children: <LogPanel />,
          },
          {
            key: 'ems',
            label: 'EMS 时段管理',
            children: <PvcPeriodView />,
          },
          {
            key: 'correct',
            label: '电量矫正',
            children: (
              <>
                <div className="panel">
                  <div className="panel-title">电量矫正<small>当电表数据发生错误时，可以使用此功能进行矫正</small></div>
                  <div className="dev-seg-bar" style={{ marginBottom: 12 }}>
                    <span className="dev-kv-k">日期</span>
                    <DatePicker size="small" value={corrDate} onChange={(d) => { setCorrDate(d); setCorrEditing(false); }} style={{ width: 150 }} />
                    <span className="dev-kv-k">电表</span>
                    <Select size="small" value={corrMeter} onChange={setCorrMeter} style={{ width: 180 }} options={dev.meters.map((m) => ({ value: m.id, label: m.name }))} />
                    {corrEditing ? (
                      <>
                        <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => setCorrSave(true)}>保存</Button>
                        <Button size="small" onClick={() => setCorrEditing(false)}>退出编辑</Button>
                      </>
                    ) : (
                      <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => setCorrEditing(true)}>编辑</Button>
                    )}
                  </div>
                  <Table
                    size="small"
                    rowKey="key"
                    pagination={false}
                    dataSource={corrRows}
                    columns={corrColumns}
                    scroll={{ x: 980 }}
                  />
                  <div className="pvc-tip-warn" style={{ marginTop: 10 }}>
                    点击"编辑"，可修改当日的电量；同时展示前一天和后一天的电量，用于对比。点击"保存"，会提示用户是否需要重新计算收益，默认勾选，则会在存储用户设置的电量的同时，重新计算对应的电表相关的收益；取消勾选则不重算收益，只修改电量。
                  </div>
                </div>
                <div className="panel">
                  <div className="panel-title">操作记录<small>电量矫正留痕</small></div>
                  <Table size="small" rowKey="key" pagination={false} dataSource={corrLogs}
                    columns={[
                      { title: '操作时间', dataIndex: 'time', width: 170 },
                      { title: '修改日期', dataIndex: 'date', width: 110 },
                      { title: '修改电表', dataIndex: 'meter', width: 160 },
                      { title: '修改内容', dataIndex: 'content' },
                    ]} />
                </div>
              </>
            ),
          },
          {
            key: 'recalc',
            label: '电量与收益重算',
            children: (
              <div className="panel">
                <div className="panel-title">电量与收益重算：</div>
                <div className="dev-seg-bar">
                  <span className="dev-kv-k">日期</span>
                  <DatePicker.RangePicker size="small" defaultValue={[dayjs('2025-05-01'), dayjs('2025-05-08')]} style={{ width: 300 }} />
                  <Button type="primary" size="small" icon={<ReloadOutlined />} onClick={() => message.success('重算任务已提交（演示）')}>重算</Button>
                </div>
                <div className="pvc-tip-warn" style={{ marginTop: 10 }}>
                  可对所选日期的电量和收益进行重算，最多可选15天
                </div>
              </div>
            ),
          },
          {
            key: 'clear',
            label: '告警清理',
            children: (
              <div className="panel">
                <div className="panel-title">告警清理<small>按时间范围查询并清空历史告警</small></div>
                <div className="dev-seg-bar" style={{ marginBottom: 14 }}>
                  <span className="dev-kv-k">时间范围：(UTC+8)</span>
                  <DatePicker.RangePicker showTime size="small" style={{ width: 340 }} />
                  <Button type="primary" size="small" onClick={() => message.info('已按时间范围查询告警（演示）')}>查询</Button>
                </div>
                <div className="dev-seg-bar">
                  <span className="dev-kv-k">告警数量：{alertCount} 条</span>
                  <Button size="small" onClick={() => { setAlertCount(0); message.success('已清空告警（演示）'); }}>清空</Button>
                </div>
              </div>
            ),
          },
          {
            key: 'tariff',
            label: '电价管理',
            children: (
              <div className="panel">
                <div className="panel-title">
                  <span>电价管理<small style={{ marginLeft: 8 }}>配置电网买入/卖出电价与光伏发电电价</small></span>
                </div>
                <ScrollTabs
                  activeKey={tariffTab}
                  onChange={setTariffTab}
                  items={[
                    {
                      key: 'grid',
                      label: '电网电价',
                      children: (
                        <>
                          <div className="dev-seg-bar" style={{ marginBottom: 12 }}>
                            <span className="dev-kv-k">年份</span>
                            <Select size="small" value={tariffYear} onChange={setTariffYear} style={{ width: 110 }}
                              options={['2023', '2024', '2025'].map((y) => ({ value: y, label: y }))} />
                            {tariffEditing ? (
                              <>
                                <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => { setTariffEditing(false); message.success('电网电价已保存（演示）'); }}>保存</Button>
                                <Button size="small" onClick={() => setTariffEditing(false)}>取消编辑</Button>
                              </>
                            ) : (
                              <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => setTariffEditing(true)}>进入编辑模式</Button>
                            )}
                          </div>
                          <Table
                            size="small"
                            rowKey="key"
                            pagination={false}
                            dataSource={tariffGrid}
                            columns={[
                              { title: '生效日期', dataIndex: 'month', width: 110 },
                              { title: '买入电价 (元/kWh)', children: CORR_PERIODS.map((p, i) => ({
                                title: p, align: 'right', width: 84,
                                render: (_, r) => tariffCell(r, 'buy', i),
                              })) },
                              { title: '卖出电价 (元/kWh)', children: CORR_PERIODS.map((p, i) => ({
                                title: p, align: 'right', width: 84,
                                render: (_, r) => tariffCell(r, 'sell', i),
                              })) },
                            ]}
                            scroll={{ x: 1000 }}
                          />
                        </>
                      ),
                    },
                    {
                      key: 'pv',
                      label: '光伏发电电价',
                      children: (
                        <>
                          <Alert type="info" showIcon style={{ marginBottom: 12 }}
                            title="此处配置的电价，用于和光伏表发电量相乘，计算光伏收益参考，并非光伏上网价格，请注意。暂时只支持长期一口价，编辑保存后立即生效。" />
                          <div className="dev-seg-bar" style={{ marginBottom: 10 }}>
                            <span className="dev-kv-k"><span style={{ color: '#ff4d4f' }}>*</span> 光伏发电电价</span>
                            <InputNumber size="small" value={pvPrice} step={0.01} min={0} onChange={setPvPrice} style={{ width: 380 }} />
                            <span className="dev-kv-k">元/kWh</span>
                          </div>
                          <div className="dev-seg-bar" style={{ marginBottom: 10 }}>
                            <span className="dev-kv-k"><span style={{ color: '#ff4d4f' }}>*</span> 是否用此电价重算历史收益</span>
                            <Radio.Group size="small" value={pvRecalc} onChange={(e) => setPvRecalc(e.target.value)}
                              options={[{ label: '否', value: false }, { label: '是', value: true }]} />
                          </div>
                          <div className="dev-seg-bar" style={{ marginBottom: 10 }}>
                            <span className="dev-kv-k"><span style={{ color: '#ff4d4f' }}>*</span> 重算日期</span>
                            <DatePicker.RangePicker size="small" style={{ width: 260 }} placeholder={['开始日期', '结束日期']} disabled={!pvRecalc} />
                          </div>
                          <div className="dev-seg-bar">
                            <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => message.success('光伏发电电价已保存（演示）')}>确定</Button>
                            <Button size="small" onClick={() => message.info('已取消修改（演示）')}>取消</Button>
                          </div>
                        </>
                      ),
                    },
                  ]}
                />
              </div>
            ),
          },
          {
            key: 'dynenv',
            label: '动环管理',
            children: <PvcDynEnvView dev={dev} />,
          },
          {
            key: 'rw',
            label: '设备读写',
            children: <PvcReadWriteView station={station} />,
          },
          {
            key: 'secpwd',
            label: '二次口令',
            children: <PvcSecPwdView />,
          },
          {
            key: 'emsreset',
            label: 'EMS复归',
            children: <PvcEmsResetView />,
          },
          {
            key: 'personal',
            label: '个性配置',
            children: <PvcPersonalView />,
          },
          {
            key: 'advanced',
            label: '功能配置开关',
            children: (
              <div className="panel">
                <div className="panel-title">功能配置开关<small>控制需量保护与动态多级保护功能是否启用</small></div>
                {[
                  {
                    name: '时段需量、逆功率保护',
                    on: adv.periodProtect.enabled,
                    desc: '开启后，可在预置策略模板中，根据时段设置需量、逆功率保护的参数\n系统会优先按照时段中的需量、逆功率保护的参数进行充放功率的约束',
                  },
                  {
                    name: '动态多级保护',
                    on: adv.dynamicMulti.enabled,
                    desc: '如需使用此功能，请先开启时段需量、逆功率保护\n如需使用动态需量，需电表支持采集【月度最大需量】测点，如有疑问，请联系技术人员',
                    warn: true,
                  },
                ].map((it) => (
                  <div key={it.name} style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
                    padding: '16px 18px', marginBottom: 12, borderRadius: 10,
                    background: 'linear-gradient(180deg, rgba(74, 58, 132, 0.55), rgba(48, 38, 92, 0.55))',
                    border: '1px solid rgba(140, 120, 220, 0.35)',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 8 }}>{it.name}</div>
                      {it.desc.split('\n').map((line, i) => (
                        <div key={i} style={{
                          fontSize: 12, lineHeight: 1.8,
                          color: it.warn && i > 0 ? '#ff7875' : 'rgba(255,255,255,0.6)',
                        }}>{line}</div>
                      ))}
                    </div>
                    <Switch defaultChecked={it.on} />
                  </div>
                ))}
              </div>
            ),
          },
          {
            key: 'proadv',
            label: '高级功能',
            children: <PvcAdvancedView />,
          },
        ]}
      />

      <Modal title="是否保存当前电量数据" open={corrSave} onCancel={() => setCorrSave(false)} destroyOnHidden
        okText="确定" cancelText="取消"
        onOk={() => {
          const summary = corrRecalc ? '充电/放电量已矫正，并重新计算收益（演示）' : '充电/放电量已矫正，未重算收益（演示）';
          setCorrLogs((ls) => [...ls, { key: ls.length + 1, time: nowStr(), date: curKey, meter: corrMeterName, content: summary }]);
          setCorrEditing(false);
          setCorrSave(false);
          message.success(summary);
        }}>
        <Checkbox checked={corrRecalc} onChange={(e) => setCorrRecalc(e.target.checked)} style={{ marginBottom: 10 }}>
          重新计算收益
        </Checkbox>
        <div className="pvc-tip-warn" style={{ marginTop: 0 }}>
          默认勾选，则会在存储用户设置的电量的同时，重新计算对应的电表相关的收益；取消勾选则不重算收益，只修改电量。
        </div>
      </Modal>
    </>
  );
};

// 日志管理（3.6.3 操作日志）
const LogPanel = () => {
  const rows = LOGS.map((r) => ({ key: r.key, time: r.time, content: `[${r.type.replace('日志', '')}] ${r.action}`, account: r.user }));
  return (
    <div className="panel">
      <ScrollTabs
        activeKey="op"
        items={[{
          key: 'op',
          label: '操作日志',
          children: (
            <>
              <div className="dev-seg-bar" style={{ marginBottom: 12 }}>
                <span className="dev-kv-k">日期</span>
                <DatePicker.RangePicker size="small" defaultValue={[dayjs('2025-05-08'), dayjs('2025-05-15')]} style={{ width: 260 }} />
                <Button type="primary" size="small" onClick={() => message.info('已按日期查询操作日志（演示）')}>搜索</Button>
                <Button size="small" onClick={() => message.info('已重置查询条件（演示）')}>重置</Button>
              </div>
              <Table
                size="small"
                rowKey="key"
                dataSource={rows}
                pagination={{ pageSize: 8, size: 'small' }}
                columns={[
                  { title: '时间', dataIndex: 'time', width: 200 },
                  { title: '操作内容', dataIndex: 'content' },
                  { title: '账号', dataIndex: 'account', width: 160 },
                ]}
              />
            </>
          ),
        }]}
      />
    </div>
  );
};

export default PvcSystem;
